import { useState, useRef, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { X, Plus, Loader2, RefreshCw, Sliders } from "lucide-react";
import { Sparkle } from "@/components/icons";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "@/components/ui/dialog";

const QUALITIES = [
  { v: "480p", l: "480p" },
  { v: "720p", l: "720p" },
];
const DURATIONS = [
  { v: 15, l: "15s", panels: 4 },
  { v: 30, l: "30s", panels: 8 },
];
const RATIOS = ["9:16", "1:1", "16:9", "4:3"];

const CREATE_EXAMPLES = [
  "@image1 = Nose character",
  "@image2 = Friend character",
  "@image1 sits on the left side of the table.",
  "@image2 sits on the right side.",
  "@image1 looks confused while @image2 explains something.",
  "Keep both characters visually consistent with their respective references.",
  "Do not merge or swap their identities.",
];

function ExamplesPopup({ open, onClose }) {
  if (!open) return null;
  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center p-4" data-testid="astro-examples-popup">
      <div className="absolute inset-0 bg-black/80 backdrop-blur-sm" onClick={onClose} />
      <div className="relative w-full max-w-md bg-ink-900 border border-zinc-800 rounded-2xl p-5">
        <div className="flex items-center justify-between mb-3">
          <h3 className="font-heading font-bold text-lg text-white">Examples</h3>
          <button onClick={onClose} data-testid="astro-examples-close" className="text-zinc-400 hover:text-white bg-ink-800 rounded-full p-1.5"><X size={18} /></button>
        </div>
        <p className="text-xs text-zinc-500 mb-3">Describe relationships between your reference images and their positions or actions. These are instructional only — they won't be added to your prompt.</p>
        <ul className="space-y-2 mb-4">
          {CREATE_EXAMPLES.map((e, i) => (
            <li key={i} className="flex gap-2 text-sm text-zinc-300"><span className="text-gold mt-1">•</span><span>{e}</span></li>
          ))}
        </ul>
        <button onClick={onClose} data-testid="astro-examples-gotit" className="w-full rounded-lg bg-gold text-ink-950 font-heading font-bold py-2.5">Got it</button>
      </div>
    </div>
  );
}

function Chip({ active, onClick, children, testid }) {
  return (
    <button onClick={onClick} data-testid={testid}
      className={`px-4 py-2 text-sm font-heading border rounded-lg transition-colors ${active ? "border-gold text-gold bg-[#1a140d]" : "border-zinc-800 text-zinc-400 hover:border-zinc-600"}`}>
      {children}
    </button>
  );
}

export default function AstroStudioModal({ open, onOpenChange }) {
  const navigate = useNavigate();
  const fileRef = useRef(null);
  const [story, setStory] = useState("");
  const [refs, setRefs] = useState([]);
  const [quality, setQuality] = useState("720p");
  const [duration, setDuration] = useState(15);
  const [ratio, setRatio] = useState("9:16");
  const [busy, setBusy] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [showExamples, setShowExamples] = useState(false);

  const panels = duration >= 30 ? 8 : 4;

  useEffect(() => {
    if (open) {
      const pre = sessionStorage.getItem("astro_prefill_ref");
      if (pre) { setRefs([{ url: pre }]); sessionStorage.removeItem("astro_prefill_ref"); }
    }
  }, [open]);

  const onUpload = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    setUploading(true);
    for (const f of files.slice(0, 5 - refs.length)) {
      try {
        const b64 = await new Promise((res) => { const rd = new FileReader(); rd.onload = () => res(rd.result); rd.readAsDataURL(f); });
        const { data } = await api.post(`/uploads/image`, { image_base64: b64, origin_url: window.location.origin });
        setRefs((r) => [...r, { url: data.url }]);
      } catch { toast.error("Upload failed"); }
    }
    setUploading(false);
    e.target.value = "";
  };

  const reset = () => { setStory(""); setRefs([]); setQuality("720p"); setDuration(15); setRatio("9:16"); };

  const astroIt = async () => {
    if (!story.trim()) { toast.error("Describe your story first"); return; }
    setBusy(true);
    try {
      const { data } = await api.post(`/astro/generate`, {
        story,
        reference_images: refs.map((r) => r.url),
        params: { quality, duration, ratio },
        origin_url: window.location.origin,
      });
      onOpenChange(false);
      reset();
      navigate(`/astro/${data.id}`);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Astro generation failed");
    } finally { setBusy(false); }
  };

  return (
    <>
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="bg-black border-zinc-800 text-[#F0E9DD] sm:max-w-md max-h-[94vh] overflow-y-auto p-0 gap-0 [&>button]:hidden">
          <DialogDescription className="sr-only">Astro Studio — story to comic panels to animation.</DialogDescription>
          <div className="flex items-center justify-between px-5 pt-5 pb-3">
            <button onClick={() => onOpenChange(false)} data-testid="astro-modal-close" className="text-gold hover:opacity-70"><X size={24} /></button>
            <DialogTitle className="font-heading font-bold text-xl"><span className="text-gold">ASTRO</span> STUDIO</DialogTitle>
            <span className="w-6" />
          </div>

          <div className="px-5 space-y-4">
            {/* references */}
            <div className="border border-zinc-800 rounded-xl p-4 bg-zinc-950/40">
              <div className="flex items-center justify-between mb-3">
                <span className="text-zinc-300 font-heading font-semibold">Reference Image <span className="text-zinc-600 font-normal">(Optional)</span></span>
                <div className="flex items-center gap-3">
                  <button onClick={() => setRefs([])} className="text-zinc-500 hover:text-gold"><RefreshCw size={15} /></button>
                  <span className="w-px h-4 bg-zinc-700" />
                  <span className="text-xs font-mono border border-zinc-700 rounded-full px-2.5 py-1 text-gold">{refs.length}/5</span>
                </div>
              </div>
              <input ref={fileRef} type="file" accept="image/*" multiple onChange={onUpload} className="hidden" data-testid="astro-ref-input" />
              <div className="flex flex-wrap gap-2">
                {refs.map((r, i) => (
                  <div key={i} className="relative w-16">
                    <div className="w-16 h-16 border border-zinc-800 rounded-md overflow-hidden">
                      <img src={r.url} alt="ref" className="w-full h-full object-cover" />
                      <button onClick={() => setRefs((x) => x.filter((_, j) => j !== i))} className="absolute top-0 right-0 bg-ink-950/80 p-0.5"><X size={12} /></button>
                    </div>
                    <span className="block text-center text-[10px] font-mono text-gold mt-0.5">@image{i + 1}</span>
                  </div>
                ))}
                {refs.length < 5 && (
                  <button onClick={() => fileRef.current?.click()} disabled={uploading} data-testid="astro-add-ref" className="w-16 h-16 border border-dashed border-zinc-600 rounded-md flex items-center justify-center text-zinc-400 hover:border-gold hover:text-gold transition-colors">
                    {uploading ? <Loader2 className="animate-spin" size={16} /> : <Plus size={22} />}
                  </button>
                )}
              </div>
            </div>

            {/* story */}
            <div className="border border-zinc-800 rounded-xl p-4 bg-zinc-950/40 min-h-[180px] flex flex-col">
              <div className="flex items-center justify-between mb-2">
                <div className="flex items-center gap-2 text-gold font-heading font-semibold"><Sparkle size={16} /> Your Story</div>
                <button onClick={() => setShowExamples(true)} data-testid="astro-examples" className="text-xs text-gold hover:underline">Examples</button>
              </div>
              <textarea value={story} onChange={(e) => setStory(e.target.value)} data-testid="astro-story-input"
                placeholder="Describe what you want to happen. Your description is the source of truth — reference relationships like @image1 sits on the left…"
                className="w-full flex-1 bg-transparent text-base focus:outline-none resize-none placeholder:text-zinc-600 leading-relaxed min-h-[110px]" />
            </div>

            {/* parameters */}
            <div className="border border-zinc-800 rounded-xl p-4 bg-zinc-950/40 space-y-4">
              <div className="flex items-center gap-2 text-gold font-heading font-semibold"><Sliders size={16} /> Generate Parameters</div>
              <div>
                <div className="text-xs text-zinc-500 mb-1.5">Video Quality</div>
                <div className="flex gap-2">
                  {QUALITIES.map((q) => <Chip key={q.v} active={quality === q.v} onClick={() => setQuality(q.v)} testid={`astro-quality-${q.v}`}>{q.l}</Chip>)}
                </div>
              </div>
              <div>
                <div className="text-xs text-zinc-500 mb-1.5">Duration <span className="text-zinc-600">· {panels} panels · ~3.75s each</span></div>
                <div className="flex gap-2">
                  {DURATIONS.map((d) => <Chip key={d.v} active={duration === d.v} onClick={() => setDuration(d.v)} testid={`astro-duration-${d.v}`}>{d.l} · {d.panels} panels</Chip>)}
                </div>
              </div>
              <div>
                <div className="text-xs text-zinc-500 mb-1.5">Ratio</div>
                <div className="flex flex-wrap gap-2">
                  {RATIOS.map((r) => <Chip key={r} active={ratio === r} onClick={() => setRatio(r)} testid={`astro-ratio-${r}`}>{r}</Chip>)}
                </div>
              </div>
            </div>
          </div>

          <div className="px-5 pt-4 pb-5">
            <button onClick={astroIt} disabled={busy} data-testid="astro-it-submit"
              className="w-full flex items-center justify-center gap-2 rounded-lg bg-gradient-to-b from-goldlight to-gold text-ink-950 font-heading font-black text-xl py-3.5 hover:brightness-105 transition disabled:opacity-60 shadow-[0_0_24px_rgba(198,138,91,0.35)]">
              {busy ? <><Loader2 className="animate-spin" size={22} /> Astro-ing…</> : <><Sparkle size={20} /> Astro It</>}
            </button>
            <p className="text-center text-[11px] text-zinc-600 mt-2">Generates a {panels}-panel comic · 15 credits</p>
          </div>
          <ExamplesPopup open={showExamples} onClose={() => setShowExamples(false)} />
        </DialogContent>
      </Dialog>
    </>
  );
}
